"use client";

import { PersonalInfoData } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { signUpCache } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { useStripFaireDirectWidgetAnalyticsQueryParams } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useStripFaireDirectWidgetAnalyticsQueryParams/useStripFaireDirectWidgetAnalyticsQueryParams";
import { assignWithLocale } from "@faire/retailer-visitor-shared/lib/assignWithLocale";
import { isCountryUSA } from "@faire/retailer-visitor-shared/lib/countryUtils";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import {
  isFaireDirectCreditEligible,
  isWidget,
  isFaireDirectRequireEmailAddressSignUpFlow,
} from "@faire/retailer-visitor-shared/lib/signUpRevampUtils";
import { isValidEmail } from "@faire/retailer-visitor-shared/lib/validation";
import { home } from "@faire/retailer-visitor-shared/paths/index";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { useSettingOwFGrant } from "@faire/retailer-visitor-shared/settings/useSettingOwFGrant";
import { useAssignSettingShopToBuyLoggedOut } from "@faire/retailer-visitor-shared/settings/useSettingShopToBuyLoggedOut";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { SignupFormContainer } from "@faire/retailer-visitor-shared/views/SignUp/_components/CommonStyles";
import { WebApiErrorMessage } from "@faire/retailer-visitor-shared/views/SignUp/_components/WebApiErrorMessage";
import {
  useSignupRetailerSubUser,
  useRetailerSubUser,
  useTrackRetailerSubUserView,
} from "@faire/retailer-visitor-shared/views/SignUp/_hooks/retailerSubUserHooks";
import { useSaveIncompleteSignup } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSaveIncompleteSignup";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { useSignUpUPLT } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpUPLT";
import { getIsOwFGrantFlow } from "@faire/retailer-visitor-shared/views/SignUp/_utils/signupUtilities";
import { useIsAppRouterMigration } from "@faire/retailer-visitor-shared/views/SignUp/_utils/useIsAppRouterMigration";
import { useIsVerticalizedSignUp } from "@faire/retailer-visitor-shared/views/SignUp/_verticalized-signup/useIsVerticalizedSignUp";
import {
  IElementsVisibility,
  PersonalInfoForm,
  useSchema,
} from "@faire/retailer-visitor-shared/views/SignUp/personal/__internal__/PersonalInfoForm";
import { PersonalInfoTitle } from "@faire/retailer-visitor-shared/views/SignUp/personal/__internal__/PersonalInfoTitle";
import { SubUserOnboardingError } from "@faire/retailer-visitor-shared/views/SignUp/personal/__internal__/sub-user/SubUserOnboardingError";
import { useSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useSaveRequestAndGoToNextStep } from "@faire/retailer-visitor-shared/views/SignUp/signupStepper";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { useSignUpPrefetchEffect } from "@faire/retailer-visitor-shared/views/SignUp/useSignUpPrefetchEffect";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web--source/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { setLocale } from "@faire/web--source/common/localization/setLocale";
import { getLocaleKey } from "@faire/web--source/common/serialized-data/getLocaleKey";
import { Column } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Spacer } from "@faire/web--source/slate/spacing";
import { useCurrentPath } from "@faire/web--source/ui/hooks/useCurrentPath";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";
import { ILocaleKey } from "@faire/web-api--source/indigofair/locale/ILocaleKey";
import deactivateRetailerPath from "@faire/web-api--source/routes/www/deactivate/r/invitationToken/verify-identity";
import { Formik } from "formik";
import { redirect } from "next/navigation";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Redirect } from "react-router-dom";

export const PersonalInfoStep: React.FC = () => {
  const isSignUpAppRotuerMigration = useIsAppRouterMigration();
  const initialValues = useInitialValues();
  const location = useLocation();
  const { queryParams } = useSignUpQueryParams();
  const currentPath = useCurrentPath();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const isOwFGrant = useSettingOwFGrant();
  const { saveAndGoToNextStep } = useSaveRequestAndGoToNextStep(
    Step.PERSONAL_INFO
  );

  useSignUpPrefetchEffect(Step.PERSONAL_INFO);

  const {
    isRetailerSubUser,
    isLoadingInvitation,
    invitationError,
    invitation,
    invitationToken,
  } = useRetailerSubUser();

  const { isSigningUp, signUpError, signUpSubUser } =
    useSignupRetailerSubUser();
  const { saveIncompleteSignupAfterPersonalStep, isLoading } =
    useSaveIncompleteSignup();
  const { isActive } = useSSOStore();
  const visibleElements = useFieldsVisibility();
  const assignSettingShopToBuyLoggedOut = useAssignSettingShopToBuyLoggedOut();

  const isSSO = isActive && !isRetailerSubUser;
  const isFD = isFaireDirectCreditEligible(location);
  const isOwFGrantFlow = getIsOwFGrantFlow(queryParams.source, isOwFGrant);

  const schema = useSchema({
    requirePassword: visibleElements.password,
    requireEmail: visibleElements.faireDirectRequiredEmailAddressSignUpElements,
  });

  useTrackRetailerSubUserView();
  const isVerticalizedSignUp = useIsVerticalizedSignUp();
  const loadTimingRef = useSignUpUPLT({
    step: Step.PERSONAL_INFO,
    isLoading: isLoading || isLoadingInvitation,
    ignoreImages:
      !visibleElements.faireDirectRequiredEmailAddressSignUpElements,
    disableUPLT: isVerticalizedSignUp || isRetailerSubUser,
    options: {
      isFD,
    },
  });

  useStripFaireDirectWidgetAnalyticsQueryParams({
    enabled: isFD,
  });

  const handleLanguageChange = useCallback(
    (
      oldLocaleKey: ILocaleKey | undefined,
      newLocaleKey: ILocaleKey | undefined,
      values: PersonalInfoData
    ) => {
      if (!isLocaleKeyEqual(oldLocaleKey, newLocaleKey) && newLocaleKey) {
        setLocale(newLocaleKey.language as Language, undefined, newLocaleKey);
        signUpCache.save(
          ISignUpRequest.build({
            ...values,
            user: IUser.build({ ...values, locale_key: newLocaleKey }),
          })
        );
        assignWithLocale(currentPath.toString());
      }
    },
    [currentPath]
  );

  const handleNext = useCallback(
    async (values: PersonalInfoData) => {
      if (invitation) {
        await signUpSubUser(values);
      } else {
        const signUpRequest =
          await saveIncompleteSignupAfterPersonalStep(values);
        saveAndGoToNextStep({ ...signUpRequest, isFD, isOwFGrantFlow });
      }
    },
    [
      invitation,
      isFD,
      isOwFGrantFlow,
      saveAndGoToNextStep,
      saveIncompleteSignupAfterPersonalStep,
      signUpSubUser,
    ]
  );

  const missingInfo =
    !queryParams.email &&
    !invitation?.invited_retailer_user?.account_user?.email &&
    !visibleElements.faireDirectRequiredEmailAddressSignUpElements;

  useEffect(() => {
    assignSettingShopToBuyLoggedOut();
  }, [assignSettingShopToBuyLoggedOut]);

  useEffect(() => {
    if (
      isSignUpAppRotuerMigration &&
      !isLoadingInvitation &&
      !invitationError
    ) {
      if (invitation?.has_existing_retailer) {
        redirect(deactivateRetailerPath(invitationToken ?? ""));
      }
      if (isLoggedInRetailer || missingInfo) {
        redirect(home());
      }
    }
  }, [
    isSignUpAppRotuerMigration,
    invitation,
    isLoggedInRetailer,
    missingInfo,
    invitationToken,
    isLoadingInvitation,
    invitationError,
  ]);

  if (isLoadingInvitation || invitationError) {
    return (
      <SignupFormContainer align="center">
        {invitationError ? (
          <SubUserOnboardingError error={invitationError} />
        ) : (
          <LoadingSpinner />
        )}
      </SignupFormContainer>
    );
  }

  if (invitation?.has_existing_retailer && !isSignUpAppRotuerMigration) {
    return <Redirect to={deactivateRetailerPath(invitationToken ?? "")} />;
  }

  if ((isLoggedInRetailer || missingInfo) && !isSignUpAppRotuerMigration) {
    return <Redirect to={home()} />;
  }

  return (
    <SignupFormContainer>
      <Column gap="4x">
        <PersonalInfoTitle
          isSSO={isSSO}
          isSubUser={isRetailerSubUser}
          isFaireDirectRequiredEmailAddressSignUpFlow={
            visibleElements.faireDirectRequiredEmailAddressSignUpElements
          }
          loadTimingRef={isFD ? loadTimingRef : undefined}
        />
        <Formik
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleNext}
        >
          <PersonalInfoForm
            visibleElements={visibleElements}
            isLoading={isLoading || isSigningUp}
            onLanguageChange={handleLanguageChange}
          />
        </Formik>
      </Column>
      {signUpError ? (
        <>
          <Spacer height="2x" />
          <WebApiErrorMessage error={signUpError} />
        </>
      ) : null}
    </SignupFormContainer>
  );
};

const useFieldsVisibility = (): IElementsVisibility => {
  const location = useLocation();
  const { isRetailerSubUser, invitation } = useRetailerSubUser();
  const { isActive, signUpSuggestions } = useSSOStore();

  const isGlobal = !isCountryUSA(
    getSessionCountry() ?? THREE_LETTER_PRIMARY_COUNTRY_CODES.USA
  );

  const isSSO = isActive && !isRetailerSubUser;
  const showNameFields = !(
    isSSO &&
    signUpSuggestions.first_name &&
    signUpSuggestions.last_name
  );
  const showPasswordField = !isSSO;
  const showEmailOptOutField = !isRetailerSubUser;
  const showSMSOptInField = !isRetailerSubUser;
  const showPrivacyPolicyNote = !!invitation?.invited_retailer_user?.role;
  const showLocaleFields = isRetailerSubUser || isGlobal;
  const showCountry = showLocaleFields && !isRetailerSubUser;
  const faireDirectRequiredEmailAddressSignUpElements =
    isWidget(location) || isFaireDirectRequireEmailAddressSignUpFlow(location);

  return useMemo(
    () => ({
      name: showNameFields,
      password: showPasswordField,
      email: isRetailerSubUser,
      emailOptOut: showEmailOptOutField,
      smsOptIn: showSMSOptInField,
      privacyPolicyNote: showPrivacyPolicyNote,
      country: showCountry,
      language: showLocaleFields,
      faireDirectRequiredEmailAddressSignUpElements,
    }),
    [
      isRetailerSubUser,
      showCountry,
      showEmailOptOutField,
      showLocaleFields,
      showNameFields,
      showPasswordField,
      showPrivacyPolicyNote,
      showSMSOptInField,
      faireDirectRequiredEmailAddressSignUpElements,
    ]
  );
};

const useInitialValues = () => {
  const [{ user, marketing_sms_preference, country: savedCountry }] =
    useSignUpRequest(["user", "marketing_sms_preference", "country"]);
  const { invitation } = useRetailerSubUser();
  const { isActive, signUpSuggestions } = useSSOStore();
  const { queryParams } = useSignUpQueryParams();
  const country =
    savedCountry ??
    queryParams.countryIso3 ??
    getSessionCountry() ??
    THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;

  const localeKey = user?.locale_key || getLocaleKey();

  const firstName =
    user?.first_name ||
    signUpSuggestions.first_name ||
    invitation?.invited_retailer_user?.account_user?.first_name ||
    "";

  const lastName =
    user?.last_name ||
    signUpSuggestions.last_name ||
    invitation?.invited_retailer_user?.account_user?.last_name ||
    "";

  const email =
    user?.email_address ||
    invitation?.invited_retailer_user?.account_user?.email ||
    (isValidEmail(queryParams.email) ? queryParams.email : undefined);

  return useMemo(
    () => ({
      first_name: firstName,
      last_name: lastName,
      email_address: email ?? "",
      password: isActive ? undefined : user?.password || "",
      country,
      phone_number: user?.phone_number,
      locale_key: localeKey,
      receiveMarketingSMSs:
        marketing_sms_preference === "SMS_PREFERENCE_OPTED_IN" || false,
      optedInToMarketingEmails: true,
    }),
    [
      country,
      email,
      firstName,
      isActive,
      lastName,
      localeKey,
      marketing_sms_preference,
      user?.password,
      user?.phone_number,
    ]
  );
};

const isLocaleKeyEqual = (
  a: ILocaleKey | undefined,
  b: ILocaleKey | undefined
) => {
  return a?.language === b?.language && a?.country === b?.country;
};
